import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from './useAccessToken';
import { useMemo, useCallback } from 'react';
import { useLogger } from '../context/useLogger';
import { AccountProfile } from '../types/profiles.types';
import {
  EdiAdminClientProfilesService,
  OAuthClientProfile,
  OAuthClientProfileRole,
} from '../services/EdiAdminClientProfilesService';

const PROFILES_QUERY_KEY = ['profiles'] as const;

const buildProfileRights = (profileRoles: OAuthClientProfileRole[]): Set<string> => {
  return new Set(profileRoles.map(r => r.RoleInstanceId).filter((id): id is string => Boolean(id)));
};

const mapOAuthProfileToAccountProfile = (profile: OAuthClientProfile): AccountProfile => {
  return {
    id: profile.InstanceId,
    name: profile.Label,
    rights: buildProfileRights(profile.Roles || []),
    default: profile.Default,
  };
};

export const useProfiles = () => {
  const getAccessToken = useAccessToken();
  const logger = useLogger();
  const queryClient = useQueryClient();

  const backendService = useMemo(
    () => new EdiAdminClientProfilesService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  // Query pour récupérer les profils
  const {
    data: profiles = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: PROFILES_QUERY_KEY,
    queryFn: async () => {
      const data = await backendService.fetchGetClientProfiles();
      return data.map(mapOAuthProfileToAccountProfile);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Mutation pour mettre à jour le profil par défaut
  const updateDefaultProfileMutation = useMutation({
    mutationFn: async (profileId: string) => {
      const profile = await backendService.fetchUpdateClientDefaultProfile(profileId);

      if (!profile) {
        throw new Error("Le profil par défaut mis à jour n'est pas valide");
      }

      return profile;
    },
    onSuccess: () => {
      // Invalide le cache pour recharger automatiquement
      void queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEY });
    },
    onError: error => {
      logger.error('Error updating default profile:', error);
    },
  });

  // Wrapper function pour garder la même API
  const updateDefaultProfile = useCallback(
    async (profileId: string): Promise<{ success: boolean; error?: string }> => {
      try {
        await updateDefaultProfileMutation.mutateAsync(profileId);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : 'Une erreur est survenue lors de la modification du profil par défaut',
        };
      }
    },
    [updateDefaultProfileMutation],
  );

  return {
    profiles,
    loading,
    error: queryError instanceof Error ? queryError.message : null,
    refetchProfiles: refetch,
    updateDefaultProfile,
    // États supplémentaires pour l'UI
    isUpdatingDefault: updateDefaultProfileMutation.isPending,
  };
};
